// src/pages/AxiomStakeholder.jsx
// AXIOM Intelligence Platform — Stakeholder Command Centers
// One corpus. Six views. Every deal.
// "CoStar for AI infrastructure" — the data moat nobody else has built.

import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Brain, Landmark, Building2, BarChart3, Server,
  Shield, Globe, TrendingUp, AlertTriangle, CheckCircle2,
  Clock, Zap, Database, ArrowRight, ChevronRight,
  Activity, Lock, FileText, Layers, Target,
  DollarSign, Percent, Calendar, Award, Network,
  AlertCircle, Info, RefreshCw, Sparkles, X,
  TrendingDown, BarChart2, PieChart, Cpu, Thermometer, Factory,
} from 'lucide-react';

// ═══════════════════════════════════════════════════════════════════════════
// POLICIES — imported inline (mirrors AxiomPolicies.jsx)
// ═══════════════════════════════════════════════════════════════════════════

const POLICIES = [
  {
    id:'SITE-001', name:'Grid Queue Threshold', category:'Site', status:'Triggered',
    description:'Fires when utility interconnect queue exceeds corpus median by >20% for the substation class.',
    trigger:'Interconnect queue > corpus median + 20%',
    corpusBasis:'31 site selections across $5B portfolio — APS, PG&E, ComEd, Dominion, ERCOT.',
    confidenceThreshold:80, savedValue:'$18M',
    lastFired:'3 days ago', lastFireProject:'Sonoran Compute Park',
    projectsAssigned:['Sonoran Compute Park','Mile High AI Hub','Midwest Sovereign Exchange'],
    projectsTriggered:['Sonoran Compute Park'],
    recommendedAction:'Advance utility pre-application immediately. Model alternative substations within 15-mile radius.',
    corpusMatches:[
      {project:'Switch PHX 1',year:2019,outcome:'Same APS substation — 22-month queue, project relocated',relevance:95},
      {project:'QTS Chandler',year:2021,outcome:'Pre-application advanced 5 months, queue reduced to 14 months',relevance:89},
    ],
  },
  {
    id:'SITE-002', name:'Latency Spec Validation', category:'Site', status:'Triggered',
    description:'Validates fiber routing from site to tenant-required exchange. Fires when modeled latency exceeds tenant SLA.',
    trigger:'Modeled latency > tenant spec threshold',
    corpusBasis:'19 financial exchange and latency-sensitive deployments. 6 fiber routing failures with remediation costs.',
    confidenceThreshold:85, savedValue:'$12M',
    lastFired:'1 day ago', lastFireProject:'Midwest Sovereign Exchange',
    projectsAssigned:['Midwest Sovereign Exchange','Mile High AI Hub'],
    projectsTriggered:['Midwest Sovereign Exchange'],
    recommendedAction:'Model 3 fiber route options immediately. Cost of resolution scales 4× after steel is set.',
    corpusMatches:[
      {project:'Equinix CH6',year:2019,outcome:'Dark fiber resolved constraint — $2M premium vs $14M rework',relevance:91},
      {project:'Iron Mountain DEN',year:2020,outcome:'Latency conflict — site abandoned, $6M sunk cost',relevance:88},
    ],
  },
  {
    id:'SITE-003', name:'Water Rights Availability', category:'Site', status:'Active',
    description:'Confirms adequate water rights for cooling load at projected MW capacity before site commitment.',
    trigger:'Water allocation < cooling load at projected MW + 15% buffer',
    corpusBasis:'24 site selections in water-constrained markets: Phoenix, Las Vegas, Denver, Dallas.',
    confidenceThreshold:90, savedValue:'$8M',
    lastFired:'14 days ago', lastFireProject:'Mile High AI Hub',
    projectsAssigned:['Mile High AI Hub','Sonoran Compute Park'],
    projectsTriggered:[],
    recommendedAction:'Confirm allocation before LOI. File water rights application concurrently with utility pre-application.',
    corpusMatches:[
      {project:'Aligned PHX 1',year:2021,outcome:'Water rights secured concurrently — no delay',relevance:93},
    ],
  },
  {
    id:'SITE-004', name:'Permitting Risk Scoring', category:'Site', status:'Monitoring',
    description:'Scores permitting risk by jurisdiction based on corpus history of approval timelines and opposition patterns.',
    trigger:'Permitting risk score < 65 for target jurisdiction',
    corpusBasis:'Permitting history across 38 jurisdictions. 11 permit delays with causal analysis.',
    confidenceThreshold:75, savedValue:'$6M avg',
    lastFired:'22 days ago', lastFireProject:'Sonoran Compute Park',
    projectsAssigned:['Sonoran Compute Park','Mile High AI Hub','Lone Star AI Campus'],
    projectsTriggered:[],
    recommendedAction:'Engage local government relations 6 months before permit filing.',
    corpusMatches:[
      {project:'CyrusOne Austin',year:2021,outcome:'Opposition delay 8 months — early engagement would have prevented',relevance:87},
    ],
  },
  {
    id:'BILD-001', name:'Vendor Lead Time Deviation', category:'Build', status:'Triggered',
    description:'Monitors critical path equipment lead times vs contract. Fires when any vendor shows >8-week deviation.',
    trigger:'Equipment lead time deviation > +8 weeks vs contract',
    corpusBasis:'47 build phases. Tracks 12 critical equipment categories: UPS, switchgear, transformers, CRAC, cooling towers.',
    confidenceThreshold:87, savedValue:'$28M (3 sites)',
    lastFired:'2 days ago', lastFireProject:'Peachtree Edge Data',
    projectsAssigned:['Lone Star AI Campus','Peachtree Edge Data','Sonoran Compute Park'],
    projectsTriggered:['Peachtree Edge Data'],
    recommendedAction:'Authorize parallel procurement from secondary vendor. Single decision protects multiple sites.',
    corpusMatches:[
      {project:'Switch LAS 2022',year:2022,outcome:'Vertiv UPS delay — 14-week slip, $22M penalty. No parallel procurement.',relevance:97},
      {project:'eBay PHX 2019',year:2019,outcome:'Early detection — Eaton substitution, zero schedule impact',relevance:94},
    ],
  },
  {
    id:'BILD-002', name:'Portfolio Equipment Pattern', category:'Build', status:'Triggered',
    description:'Detects the same equipment failure, delay, or defect pattern across 2+ portfolio sites simultaneously.',
    trigger:'Same equipment SKU + delay signature at ≥2 active sites',
    corpusBasis:'Cross-portfolio correlation engine trained on $5B procurement, delivery, and substitution history.',
    confidenceThreshold:85, savedValue:'$28M combined',
    lastFired:'2 days ago', lastFireProject:'3 sites (Dallas, Atlanta, Seattle)',
    projectsAssigned:['Lone Star AI Campus','Peachtree Edge Data'],
    projectsTriggered:['Peachtree Edge Data'],
    recommendedAction:'Treat as portfolio-level emergency. Single escalation and procurement decision protects all affected sites.',
    corpusMatches:[
      {project:'Aligned Portfolio 2022',year:2022,outcome:'Detected Generac generator delay across 4 sites — consolidated procurement saved 8 weeks',relevance:94},
    ],
  },
  {
    id:'BILD-003', name:'RFI Escalation Threshold', category:'Build', status:'Monitoring',
    description:'Tracks open RFI count, age, and criticality. P1 RFI count is the strongest predictor of commissioning slip.',
    trigger:'Open P1 RFIs > 25 with >14 days average age during Build phase',
    corpusBasis:'39 build phases. P1 RFI count at Build 70% completion is #1 predictor of commissioning slip.',
    confidenceThreshold:82, savedValue:'$4M avg',
    lastFired:'5 days ago', lastFireProject:'Peachtree Edge Data',
    projectsAssigned:['Peachtree Edge Data','Lone Star AI Campus','Midwest Sovereign Exchange'],
    projectsTriggered:[],
    recommendedAction:'Convene RFI triage session within 48 hours. Prioritize top 10 P1s for immediate resolution.',
    corpusMatches:[
      {project:'eBay SLC 2018',year:2018,outcome:'42 open P1 RFIs at Build 65% — commissioning slipped 18 days',relevance:91},
    ],
  },
  {
    id:'BILD-004', name:'MEP vs Schedule Variance', category:'Build', status:'Active',
    description:'Monitors mechanical, electrical, and plumbing progress against milestone schedule.',
    trigger:'MEP progress > 8% behind schedule milestone',
    corpusBasis:'31 build phases. MEP lag at 50% build milestone has 78% correlation with commissioning slippage.',
    confidenceThreshold:78, savedValue:'$3M avg',
    lastFired:'8 days ago', lastFireProject:'Peachtree Edge Data',
    projectsAssigned:['Peachtree Edge Data','Sonoran Compute Park'],
    projectsTriggered:[],
    recommendedAction:'Increase crew density on lagging MEP trades. MEP lag at 50% is recoverable at 2× cost.',
    corpusMatches:[
      {project:'QTS Richmond',year:2020,outcome:'MEP lag unaddressed — 3-week commissioning slip, $6M cost',relevance:84},
    ],
  },
  {
    id:'COMM-001', name:'IST Sequence Conflict', category:'Commission', status:'Triggered',
    description:'Detects IST scheduling conflicts — CRAC windows overlapping with electrical IST sequencing.',
    trigger:'IST window overlap > 5 days between any two mechanical/electrical systems on critical path',
    corpusBasis:'47 commissioning sequences. IST conflicts are #1 cause of final-mile slippage — 23% of projects.',
    confidenceThreshold:88, savedValue:'$42M',
    lastFired:'1 day ago', lastFireProject:'Lone Star AI Campus',
    projectsAssigned:['Lone Star AI Campus'],
    projectsTriggered:['Lone Star AI Campus'],
    recommendedAction:'Authorize parallel IST tracks. Dedicate separate crews to conflicting systems.',
    corpusMatches:[
      {project:'eBay Salt Lake City',year:2018,outcome:'Parallel IST resolved 21-day slip in 8 days via surge protocol',relevance:98},
      {project:'Switch LAS 4',year:2020,outcome:'CRAC/electrical conflict — 14-day slip, $28M SLA penalty',relevance:91},
    ],
  },
  {
    id:'COMM-002', name:'P1 Punch List · SLA Window', category:'Commission', status:'Triggered',
    description:'Monitors open P1 punch list items relative to remaining SLA window.',
    trigger:'P1 punch close rate insufficient to clear list before SLA date at current velocity',
    corpusBasis:'41 commissioning closeouts. P1 item count at T-45 days to SLA is the strongest leading indicator.',
    confidenceThreshold:91, savedValue:'Included in COMM-001',
    lastFired:'1 day ago', lastFireProject:'Lone Star AI Campus',
    projectsAssigned:['Lone Star AI Campus'],
    projectsTriggered:['Lone Star AI Campus'],
    recommendedAction:'Convene daily P1 war room. SLA breach cost is non-linear — early intervention is 10× cheaper.',
    corpusMatches:[
      {project:'Aligned Dallas 2022',year:2022,outcome:'Daily war room at T-38 — all P1s cleared, no SLA breach',relevance:93},
      {project:'CyrusOne Phoenix',year:2021,outcome:'41 P1s at T-30 — SLA breached by 9 days, $18M penalty',relevance:88},
    ],
  },
  {
    id:'COMM-003', name:'Surge Crew Authorization', category:'Commission', status:'Active',
    description:'Pre-authorizes surge crew mobilization when commissioning risk score drops below threshold.',
    trigger:'Commission risk score < 45 AND days to SLA < 50',
    corpusBasis:'19 commissioning interventions. Avg time from detection to surge auth: 4.2 days vs 0.3 days with pre-auth.',
    confidenceThreshold:90, savedValue:'Decision latency: 3.9 days recovered',
    lastFired:'Not yet fired', lastFireProject:'—',
    projectsAssigned:['Lone Star AI Campus'],
    projectsTriggered:[],
    recommendedAction:'Pre-authorize surge crew to mobilize within 24 hours. Standing PO with 3 crews recommended.',
    corpusMatches:[
      {project:'eBay SLC 2018',year:2018,outcome:'Pre-authorized surge crew mobilized in 6 hours — 19 days recovered',relevance:96},
    ],
  },
  {
    id:'CPTL-001', name:'Exit Window Optimization', category:'Capital', status:'Active',
    description:'Models hyperscaler capex cycles and fund deployment windows to identify peak exit timing.',
    trigger:'Demand cycle model scores exit window ≥ 85 AND asset governance readiness ≥ 80',
    corpusBasis:'12 comparable transactions: Switch, QTS, CyrusOne, Aligned, Iron Mountain REIT. Timing premium: 15-22%.',
    confidenceThreshold:88, savedValue:'$180M+ uplift',
    lastFired:'7 days ago', lastFireProject:'Virginia Hyperscale Cluster',
    projectsAssigned:['Virginia Hyperscale Cluster'],
    projectsTriggered:[],
    recommendedAction:'Initiate sale process 6 months before optimal window. PUE optimization adds 2-4% to exit multiple.',
    corpusMatches:[
      {project:'Switch REIT IPO',year:2017,outcome:'11× EBITDA — peak demand cycle exit',relevance:96},
      {project:'QTS Blackstone',year:2021,outcome:'28× EBITDA — AI demand surge premium',relevance:92},
    ],
  },
  {
    id:'CPTL-002', name:'PUE Optimization Opportunity', category:'Capital', status:'Active',
    description:'Identifies when PUE is above corpus benchmark. Quantifies exit valuation impact before go-to-market.',
    trigger:'PUE actual > PUE corpus benchmark by > 0.04 for cooling configuration',
    corpusBasis:'28 operating assets. RL thermal controls show avg 0.06 PUE improvement. Pre-exit adds 2-3% to multiple.',
    confidenceThreshold:85, savedValue:'$2.8M exit uplift',
    lastFired:'7 days ago', lastFireProject:'Virginia Hyperscale Cluster',
    projectsAssigned:['Virginia Hyperscale Cluster'],
    projectsTriggered:[],
    recommendedAction:'Implement RL thermal controls 6+ months before exit. NPV of optimization is typically 8-12× at exit.',
    corpusMatches:[
      {project:'QTS Richmond Ops',year:2020,outcome:'RL controls: PUE 1.42→1.36, $410K/yr, +2.1% transaction multiple',relevance:92},
    ],
  },
  {
    id:'CPTL-003', name:'Budget Variance Escalation', category:'Capital', status:'Monitoring',
    description:'Escalates when project budget variance exceeds thresholds by phase. Early variance is recoverable.',
    trigger:'Budget variance > +1.5% (Site/Design) | > +2.5% (Build) | > +1.0% (Commission)',
    corpusBasis:'52 project completions. Commission phase variance has highest NPV impact per dollar.',
    confidenceThreshold:82, savedValue:'$2M avg',
    lastFired:'4 days ago', lastFireProject:'Lone Star AI Campus',
    projectsAssigned:['Lone Star AI Campus','Peachtree Edge Data','Midwest Sovereign Exchange'],
    projectsTriggered:[],
    recommendedAction:'Convene budget review within 5 business days. Commission phase: prioritize recovery over cost control.',
    corpusMatches:[
      {project:'Aligned build portfolio',year:2022,outcome:'Early variance detection: 94% of projects held within 3% of budget',relevance:88},
    ],
  },
  {
    id:'EXIT-001', name:'Governance Readiness Score', category:'Exit', status:'Active',
    description:'Tracks IPO and strategic transaction governance readiness: board composition, audit cleanliness, ESG.',
    trigger:'Exit window within 18 months AND governance score < 80',
    corpusBasis:'9 completed exits. Governance gaps in due diligence add 60-90 days to close and reduce multiple 4-8%.',
    confidenceThreshold:90, savedValue:'Multiple protection: 4-8%',
    lastFired:'14 days ago', lastFireProject:'Virginia Hyperscale Cluster',
    projectsAssigned:['Virginia Hyperscale Cluster'],
    projectsTriggered:[],
    recommendedAction:'Begin governance prep 18 months before exit. Prioritize: independent board seats, 3-year audited financials.',
    corpusMatches:[
      {project:'Switch IPO 2017',year:2017,outcome:'Clean governance — IPO at top of range',relevance:94},
      {project:'QTS strategic 2021',year:2021,outcome:'30-day DD extension due to board structure — $120M haircut',relevance:89},
    ],
  },
  {
    id:'EXIT-002', name:'Strategic Buyer Identification', category:'Exit', status:'Active',
    description:'Monitors infrastructure fund mandates, REIT capital cycles, and hyperscaler M&A signals.',
    trigger:'Asset meets exit readiness AND ≥2 identified buyers with active mandates',
    corpusBasis:'Buyer mandate tracking across 14 infra funds, 6 REITs, and 4 hyperscaler corporate dev teams.',
    confidenceThreshold:78, savedValue:'Process premium: 8-15%',
    lastFired:'7 days ago', lastFireProject:'Virginia Hyperscale Cluster',
    projectsAssigned:['Virginia Hyperscale Cluster'],
    projectsTriggered:[],
    recommendedAction:'Build buyer relationships 12+ months before process. Provide data room access 6 months early for competitive tension.',
    corpusMatches:[
      {project:'Aligned exit 2025',year:2025,outcome:'3 competing bidders → $40B — 22% premium vs single-buyer process',relevance:91},
    ],
  },
];

// ═══════════════════════════════════════════════════════════════════════════
// STAKEHOLDER CONFIG
// ═══════════════════════════════════════════════════════════════════════════

const STAKEHOLDERS = [
  {
    id: 'owners_rep',
    label: "Owner's Rep",
    tagline: 'Full lifecycle command. One source of truth.',
    Icon: Landmark,
    accent: { from:'#3b82f6', to:'#6366f1' },
    ring: 'ring-blue-500/40',
    badge: 'bg-blue-500/15 text-blue-300 border-blue-500/25',
    activeBg: 'from-blue-600 to-indigo-600',
    dot: 'bg-blue-500',
    textAccent: 'text-blue-400',
    borderAccent: 'border-blue-500/30',
    phases: ['Site','Build','Commission','Capital','Exit'],
    heroStat: { num:'$280M+', label:'Value Protected', color:'text-blue-300' },
    pitch: 'Replaces spreadsheets with governed AI across every phase of gigawatt AI factory development. Every decision traces to a corpus citation.',
  },
  {
    id: 'developer',
    label: 'Developer',
    tagline: 'Build execution. Risk before it compounds.',
    Icon: Building2,
    accent: { from:'#f97316', to:'#f59e0b' },
    ring: 'ring-orange-500/40',
    badge: 'bg-orange-500/15 text-orange-300 border-orange-500/25',
    activeBg: 'from-orange-600 to-amber-600',
    dot: 'bg-orange-500',
    textAccent: 'text-orange-400',
    borderAccent: 'border-orange-500/30',
    phases: ['Site','Build','Commission'],
    heroStat: { num:'78%', label:'MEP Slip Correlation Identified Early', color:'text-orange-300' },
    pitch: 'De-risk site selection and build execution. The AI knows more than any individual consultant — trained on actual project decisions, not templates.',
  },
  {
    id: 'fund',
    label: 'Infra Fund',
    tagline: 'Exit timing. Portfolio benchmarking. Capital deployment.',
    Icon: BarChart3,
    accent: { from:'#a78bfa', to:'#c084fc' },
    ring: 'ring-violet-500/40',
    badge: 'bg-violet-500/15 text-violet-300 border-violet-500/25',
    activeBg: 'from-violet-600 to-fuchsia-600',
    dot: 'bg-violet-500',
    textAccent: 'text-violet-400',
    borderAccent: 'border-violet-500/30',
    phases: ['Capital','Exit'],
    heroStat: { num:'$180M+', label:'Exit Timing Uplift Modeled', color:'text-violet-300' },
    pitch: 'Benchmark portfolios, time exits, evaluate development partners. Same corpus — different policy lens. Compounding network effects with every new deal.',
  },
  {
    id: 'hyperscaler',
    label: 'Hyperscaler',
    tagline: 'Gigawatt delivery. SLA protection. Supply chain signals.',
    Icon: Cpu,
    accent: { from:'#22d3ee', to:'#38bdf8' },
    ring: 'ring-cyan-500/40',
    badge: 'bg-cyan-500/15 text-cyan-300 border-cyan-500/25',
    activeBg: 'from-cyan-600 to-sky-600',
    dot: 'bg-cyan-500',
    textAccent: 'text-cyan-400',
    borderAccent: 'border-cyan-500/30',
    phases: ['Build','Commission'],
    heroStat: { num:'19,200', label:'GPUs Governed Across Campus', color:'text-cyan-300' },
    pitch: 'Evaluate development partners, monitor supply chains, track gigawatt campus progress against benchmarks. 90 seconds from power-on to first asset discovery.',
  },
  {
    id: 'lender',
    label: 'Lender + Insurer',
    tagline: 'Signal-based risk. Not spreadsheet snapshots.',
    Icon: Shield,
    accent: { from:'#10b981', to:'#059669' },
    ring: 'ring-emerald-500/40',
    badge: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/25',
    activeBg: 'from-emerald-600 to-teal-600',
    dot: 'bg-emerald-500',
    textAccent: 'text-emerald-400',
    borderAccent: 'border-emerald-500/30',
    phases: ['Build','Commission','Capital'],
    heroStat: { num:'52', label:'Project Completions in Corpus', color:'text-emerald-300' },
    pitch: 'Underwrite construction and operational risk from governed signals — not spreadsheet snapshots. Every signal traces to a policy ID and a corpus citation.',
  },
  {
    id: 'government',
    label: 'Government',
    tagline: 'Infrastructure quality. Standardized assessment.',
    Icon: Globe,
    accent: { from:'#f43f5e', to:'#e11d48' },
    ring: 'ring-rose-500/40',
    badge: 'bg-rose-500/15 text-rose-300 border-rose-500/25',
    activeBg: 'from-rose-600 to-red-600',
    dot: 'bg-rose-500',
    textAccent: 'text-rose-400',
    borderAccent: 'border-rose-500/30',
    phases: ['Site','Build','Commission'],
    heroStat: { num:'38', label:'Jurisdictions in Corpus', color:'text-rose-300' },
    pitch: 'Assess AI infrastructure quality and readiness with standardized policy views over institutional memory. Same corpus, public-interest policy lens.',
  },
];

// ═══════════════════════════════════════════════════════════════════════════
// UTILS
// ═══════════════════════════════════════════════════════════════════════════

const CAT_COLORS = {
  Site:       { text:'text-sky-400',    bg:'bg-sky-500/12',    border:'border-sky-500/25',    dot:'bg-sky-400',    bar:'#38bdf8' },
  Build:      { text:'text-orange-400', bg:'bg-orange-500/12', border:'border-orange-500/25', dot:'bg-orange-400', bar:'#fb923c' },
  Commission: { text:'text-red-400',    bg:'bg-red-500/12',    border:'border-red-500/25',    dot:'bg-red-400',    bar:'#f87171' },
  Capital:    { text:'text-violet-400', bg:'bg-violet-500/12', border:'border-violet-500/25', dot:'bg-violet-400', bar:'#a78bfa' },
  Exit:       { text:'text-fuchsia-400',bg:'bg-fuchsia-500/12',border:'border-fuchsia-500/25',dot:'bg-fuchsia-400',bar:'#e879f9' },
};

const STATUS = {
  Triggered:  { dot:'bg-red-400 animate-pulse',  text:'text-red-400',     bg:'bg-red-500/10',     border:'border-red-500/25',     label:'TRIGGERED' },
  Active:     { dot:'bg-emerald-400',             text:'text-emerald-400', bg:'bg-emerald-500/10', border:'border-emerald-500/25', label:'ACTIVE' },
  Monitoring: { dot:'bg-amber-400',               text:'text-amber-400',   bg:'bg-amber-500/10',   border:'border-amber-500/25',   label:'MONITORING' },
  Inactive:   { dot:'bg-slate-600',               text:'text-slate-500',   bg:'bg-slate-800/40',   border:'border-slate-700/30',   label:'INACTIVE' },
};

// Micro bar chart for relevance
const RelevanceBar = ({ value, color = '#6366f1' }) => (
  <div className="flex items-center gap-1.5">
    <div className="w-16 h-1 bg-slate-700 rounded-full overflow-hidden">
      <div className="h-full rounded-full" style={{ width:`${value}%`, background: color }} />
    </div>
    <span className="text-[9px] text-slate-500 font-mono">{value}%</span>
  </div>
);

// Animated counter
const AnimCounter = ({ target, suffix = '', prefix = '', className = '' }) => {
  const [val, setVal] = useState(0);
  const ref = useRef(null);
  useEffect(() => {
    const num = parseFloat(target.toString().replace(/[^0-9.]/g,'')) || 0;
    let start = 0;
    const step = num / 40;
    const timer = setInterval(() => {
      start = Math.min(start + step, num);
      setVal(start);
      if (start >= num) clearInterval(timer);
    }, 25);
    return () => clearInterval(timer);
  }, [target]);
  const fmt = (n) => {
    const s = target.toString();
    if (s.includes('$')) return `$${Math.round(n).toLocaleString()}`;
    if (s.includes('.')) return n.toFixed(1);
    return Math.round(n).toLocaleString();
  };
  return <span className={className}>{prefix}{fmt(val)}{suffix}</span>;
};

// Phase pill
const PhasePill = ({ phase, count }) => {
  const c = CAT_COLORS[phase] || CAT_COLORS.Site;
  return (
    <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg border ${c.bg} ${c.border}`}>
      <div className={`w-1.5 h-1.5 rounded-full ${c.dot}`} />
      <span className={`text-[10px] font-bold uppercase tracking-wider ${c.text}`}>{phase}</span>
      {count !== undefined && <span className="text-[9px] text-slate-500 font-mono">{count}</span>}
    </div>
  );
};

// Policy micro card
const PolicyMicro = ({ policy, accentColor, onClick }) => {
  const st = STATUS[policy.status] || STATUS.Active;
  const cat = CAT_COLORS[policy.category] || CAT_COLORS.Site;
  return (
    <motion.div
      onClick={() => onClick?.(policy)}
      className={`bg-slate-800/50 border border-slate-700/50 rounded-xl p-3.5 cursor-pointer transition-all hover:bg-slate-800/80 hover:border-slate-600/70 hover:translate-x-0.5`}
      whileHover={{ scale: 1.01 }}
      layout
    >
      <div className="flex items-start justify-between gap-2 mb-2">
        <span className={`text-[9px] font-mono font-bold px-1.5 py-0.5 rounded border ${cat.bg} ${cat.border} ${cat.text}`}>{policy.id}</span>
        <div className={`flex items-center gap-1 px-1.5 py-0.5 rounded-full border ${st.bg} ${st.border}`}>
          <div className={`w-1.5 h-1.5 rounded-full ${st.dot}`} />
          <span className={`text-[8px] font-bold uppercase ${st.text}`}>{st.label}</span>
        </div>
      </div>
      <div className="text-xs font-semibold text-white mb-1 leading-snug">{policy.name}</div>
      <div className="text-[10px] text-slate-500 leading-relaxed line-clamp-2 mb-2">{policy.description}</div>
      <div className="flex items-center justify-between">
        <span className="text-[9px] text-slate-600 font-mono">{policy.projectsAssigned.length} projects</span>
        <span className="text-[9px] font-bold text-emerald-400">{policy.savedValue}</span>
      </div>
    </motion.div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// POLICY DETAIL DRAWER (shared across all views)
// ═══════════════════════════════════════════════════════════════════════════

const PolicyDrawer = ({ policy, onClose, accentColor = '#6366f1' }) => {
  if (!policy) return null;
  const st = STATUS[policy.status] || STATUS.Active;
  const cat = CAT_COLORS[policy.category] || CAT_COLORS.Site;

  return (
    <>
      <motion.div className="fixed inset-0 z-40 bg-slate-950/60 backdrop-blur-sm"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        onClick={onClose}
      />
      <motion.div
        className="fixed top-0 right-0 h-full w-[480px] max-w-[92vw] bg-slate-900 border-l border-slate-700/60 z-50 flex flex-col overflow-y-auto"
        initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }}
        transition={{ type: 'spring', stiffness: 300, damping: 32 }}
      >
        {/* Header */}
        <div className="p-5 border-b border-slate-700/60 flex-shrink-0 sticky top-0 bg-slate-900 z-10">
          <div className="flex items-start justify-between gap-3 mb-3">
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`text-[10px] font-mono font-bold px-2 py-0.5 rounded border ${cat.bg} ${cat.border} ${cat.text}`}>{policy.id}</span>
              <div className={`flex items-center gap-1.5 px-2 py-0.5 rounded-full border ${st.bg} ${st.border}`}>
                <div className={`w-1.5 h-1.5 rounded-full ${st.dot}`} />
                <span className={`text-[9px] font-bold uppercase tracking-wider ${st.text}`}>{st.label}</span>
              </div>
            </div>
            <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-xl bg-slate-800 text-slate-400 hover:text-white flex-shrink-0 transition-colors">
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="text-base font-bold text-white mb-1">{policy.name}</div>
          <div className={`text-[10px] font-semibold uppercase tracking-wider ${cat.text}`}>{policy.category} Policy</div>
          <p className="text-xs text-slate-400 leading-relaxed mt-2">{policy.description}</p>
        </div>

        <div className="p-5 space-y-4">
          {/* Metrics row */}
          <div className="grid grid-cols-3 gap-3">
            {[
              { val: `${policy.confidenceThreshold}%`, label: 'Confidence', color: 'text-white' },
              { val: policy.projectsAssigned.length, label: 'Projects', color: 'text-indigo-300' },
              { val: policy.projectsTriggered.length || '0', label: 'Triggered', color: policy.projectsTriggered.length ? 'text-red-400' : 'text-slate-500' },
            ].map(({ val, label, color }) => (
              <div key={label} className="bg-slate-800/60 rounded-xl p-3 text-center">
                <div className={`text-xl font-bold ${color}`}>{val}</div>
                <div className="text-[9px] text-slate-600 uppercase tracking-wider mt-0.5">{label}</div>
              </div>
            ))}
          </div>

          {/* Trigger */}
          <div className="bg-slate-800/60 rounded-xl p-4">
            <div className="text-[9px] text-slate-500 uppercase tracking-wider font-semibold mb-2">Trigger Condition</div>
            <div className="text-xs text-slate-200 font-mono leading-relaxed bg-slate-900/60 rounded-lg px-3 py-2.5">{policy.trigger}</div>
          </div>

          {/* Corpus basis */}
          <div className="bg-slate-800/60 rounded-xl p-4">
            <div className="text-[9px] text-slate-500 uppercase tracking-wider font-semibold mb-2">Corpus Basis</div>
            <p className="text-xs text-slate-300 leading-relaxed">{policy.corpusBasis}</p>
          </div>

          {/* Last fired + value */}
          <div className="bg-slate-800/40 border border-slate-700/40 rounded-xl p-4">
            <div className="text-[9px] text-slate-500 uppercase tracking-wider font-semibold mb-2">Last Fired</div>
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm font-semibold text-white">{policy.lastFireProject}</div>
                <div className="text-xs text-slate-500 mt-0.5">{policy.lastFired}</div>
              </div>
              <div className="text-right">
                <div className="text-[9px] text-slate-600">Value protected</div>
                <div className="text-sm font-bold text-emerald-400">{policy.savedValue}</div>
              </div>
            </div>
          </div>

          {/* Corpus matches */}
          <div>
            <div className="text-[9px] text-slate-500 uppercase tracking-wider font-semibold mb-2">Corpus Matches</div>
            <div className="space-y-2">
              {policy.corpusMatches.map((m, i) => (
                <div key={i} className="bg-slate-800/40 border border-slate-700/40 rounded-xl px-4 py-3">
                  <div className="flex items-center justify-between mb-1.5">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-semibold text-white">{m.project}</span>
                      <span className="text-[9px] text-slate-600">{m.year}</span>
                    </div>
                    <RelevanceBar value={m.relevance} color={cat.bar} />
                  </div>
                  <div className="text-xs text-slate-400 leading-relaxed">{m.outcome}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Recommended action */}
          <div className={`rounded-xl p-4 border ${cat.bg} ${cat.border}`}>
            <div className={`text-[9px] font-bold uppercase tracking-wider mb-2 ${cat.text}`}>Recommended Action</div>
            <p className="text-xs text-slate-300 leading-relaxed">{policy.recommendedAction}</p>
          </div>

          {/* Projects */}
          <div>
            <div className="text-[9px] text-slate-500 uppercase tracking-wider font-semibold mb-2">Assigned Projects</div>
            <div className="space-y-1.5">
              {policy.projectsAssigned.map((proj, i) => {
                const fired = policy.projectsTriggered.includes(proj);
                return (
                  <div key={i} className={`flex items-center justify-between px-3 py-2 rounded-lg border ${fired ? 'bg-red-500/8 border-red-500/20' : 'bg-slate-800/40 border-slate-700/40'}`}>
                    <div className="flex items-center gap-2">
                      <div className={`w-1.5 h-1.5 rounded-full ${fired ? 'bg-red-400 animate-pulse' : 'bg-emerald-400'}`} />
                      <span className="text-xs text-slate-300">{proj}</span>
                    </div>
                    <span className={`text-[9px] font-semibold uppercase ${fired ? 'text-red-400' : 'text-emerald-500'}`}>{fired ? 'Triggered' : 'Monitoring'}</span>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="text-center text-[9px] text-slate-700 uppercase tracking-wider pb-2">
            Axiom Policy Engine · $5B corpus basis · 15+ hyperscale builds
          </div>
        </div>
      </motion.div>
    </>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// VIEW 1 — OWNER'S REP: Full lifecycle command
// ═══════════════════════════════════════════════════════════════════════════

const OwnersRepView = ({ onPolicyClick }) => {
  const triggered = POLICIES.filter(p => p.status === 'Triggered');
  const phases = ['Site','Build','Commission','Capital','Exit'];

  return (
    <div className="space-y-6">
      {/* Top domino alert */}
      {triggered.length > 0 && (
        <motion.div initial={{ opacity:0, y:-8 }} animate={{ opacity:1, y:0 }} transition={{ duration:0.3 }}
          className="bg-gradient-to-r from-red-950/80 to-red-900/60 border border-red-500/30 rounded-2xl p-5 flex items-start gap-4">
          <div className="w-10 h-10 bg-red-500/20 rounded-xl flex items-center justify-center flex-shrink-0 border border-red-500/30">
            <AlertTriangle className="w-5 h-5 text-red-400" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-red-400">Top Domino — Action Required</div>
              <div className="w-1.5 h-1.5 rounded-full bg-red-400 animate-pulse" />
            </div>
            <div className="text-sm font-semibold text-white mb-1">{triggered[0].name} · {triggered[0].lastFireProject}</div>
            <div className="text-xs text-red-200/70">{triggered[0].recommendedAction.slice(0, 90)}…</div>
          </div>
          <div className="text-right flex-shrink-0">
            <div className="text-[9px] text-slate-600">Value at risk</div>
            <div className="text-base font-bold text-emerald-400">{triggered[0].savedValue}</div>
          </div>
        </motion.div>
      )}

      {/* Phase-by-phase lifecycle grid */}
      <div>
        <div className="flex items-center gap-2 mb-4">
          <Layers className="w-4 h-4 text-blue-400" />
          <span className="text-sm font-bold text-white">Lifecycle Policy Status</span>
          <span className="text-xs text-slate-500 ml-1">— all 16 policies across 5 phases</span>
        </div>
        <div className="grid grid-cols-5 gap-3">
          {phases.map(phase => {
            const ps = POLICIES.filter(p => p.category === phase);
            const trig = ps.filter(p => p.status === 'Triggered').length;
            const active = ps.filter(p => p.status === 'Active').length;
            const mon = ps.filter(p => p.status === 'Monitoring').length;
            const cat = CAT_COLORS[phase];
            return (
              <motion.div key={phase}
                className={`bg-slate-800/50 border ${cat.border} rounded-2xl p-4`}
                initial={{ opacity:0, y:12 }} animate={{ opacity:1, y:0 }}
                transition={{ delay: phases.indexOf(phase) * 0.06 }}
              >
                <div className={`text-[10px] font-bold uppercase tracking-widest ${cat.text} mb-3`}>{phase}</div>
                <div className="text-2xl font-bold text-white mb-3">{ps.length}</div>
                <div className="space-y-1.5">
                  {trig > 0 && (
                    <div className="flex items-center gap-1.5">
                      <div className="w-1.5 h-1.5 rounded-full bg-red-400 animate-pulse" />
                      <span className="text-[10px] text-red-400 font-semibold">{trig} triggered</span>
                    </div>
                  )}
                  {active > 0 && (
                    <div className="flex items-center gap-1.5">
                      <div className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                      <span className="text-[10px] text-emerald-400">{active} active</span>
                    </div>
                  )}
                  {mon > 0 && (
                    <div className="flex items-center gap-1.5">
                      <div className="w-1.5 h-1.5 rounded-full bg-amber-400" />
                      <span className="text-[10px] text-amber-400">{mon} watching</span>
                    </div>
                  )}
                </div>
                <div className="mt-3 h-px bg-slate-700/60" />
                <div className="mt-2 text-[9px] text-slate-600 font-mono">
                  {POLICIES.filter(p => p.category === phase).reduce((sum, p) => sum + p.savedValue.replace(/[^0-9]/g,'') ? 1 : 0, 0)} policies active
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* All triggered policies */}
      {triggered.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-3">
            <AlertCircle className="w-4 h-4 text-red-400" />
            <span className="text-sm font-bold text-white">Triggered — Immediate Action</span>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {triggered.map(p => <PolicyMicro key={p.id} policy={p} onClick={onPolicyClick} />)}
          </div>
        </div>
      )}

      {/* Value protection summary */}
      <div className="bg-gradient-to-br from-slate-800/60 to-slate-900/60 border border-slate-700/40 rounded-2xl p-5">
        <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-4">Portfolio Value Protection · $5B Corpus Basis</div>
        <div className="grid grid-cols-4 gap-4">
          {[
            { label: 'Total Policies', val: 16, color: 'text-white' },
            { label: 'Triggered Now', val: triggered.length, color: 'text-red-400' },
            { label: 'Value Protected', val: '$280M+', color: 'text-emerald-400' },
            { label: 'Corpus Projects', val: 52, color: 'text-indigo-300' },
          ].map(({ label, val, color }) => (
            <div key={label} className="text-center">
              <div className={`text-xl font-bold ${color}`}>{val}</div>
              <div className="text-[9px] text-slate-600 uppercase tracking-wider mt-1">{label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* All policies grid */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <Database className="w-4 h-4 text-blue-400" />
          <span className="text-sm font-bold text-white">All 16 Policies</span>
        </div>
        <div className="grid grid-cols-2 gap-3">
          {POLICIES.map((p, i) => (
            <motion.div key={p.id}
              initial={{ opacity:0, y:8 }} animate={{ opacity:1, y:0 }} transition={{ delay: i * 0.03 }}
            >
              <PolicyMicro policy={p} onClick={onPolicyClick} />
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// VIEW 2 — DEVELOPER: Build execution & risk timeline
// ═══════════════════════════════════════════════════════════════════════════

const DeveloperView = ({ onPolicyClick }) => {
  const buildPolicies = POLICIES.filter(p => ['Site','Build','Commission'].includes(p.category));
  const triggered = buildPolicies.filter(p => p.status === 'Triggered');

  const riskItems = [
    { label: 'Grid queue risk', project: 'Sonoran Compute Park', severity: 'CRITICAL', policy: 'SITE-001', days: 0 },
    { label: 'Latency spec conflict', project: 'Midwest Sovereign Exchange', severity: 'CRITICAL', policy: 'SITE-002', days: 1 },
    { label: 'UPS lead time deviation', project: 'Peachtree Edge Data', severity: 'HIGH', policy: 'BILD-001', days: 2 },
    { label: 'Portfolio equipment pattern', project: '3 sites simultaneous', severity: 'HIGH', policy: 'BILD-002', days: 2 },
    { label: 'IST sequence conflict', project: 'Lone Star AI Campus', severity: 'CRITICAL', policy: 'COMM-001', days: 3 },
    { label: 'P1 punch list velocity', project: 'Lone Star AI Campus', severity: 'HIGH', policy: 'COMM-002', days: 3 },
    { label: 'Open RFI threshold', project: 'Peachtree Edge Data', severity: 'MEDIUM', policy: 'BILD-003', days: 5 },
    { label: 'MEP schedule variance', project: 'Sonoran Compute Park', severity: 'MEDIUM', policy: 'BILD-004', days: 8 },
  ];

  const projects = [
    { name: 'Lone Star AI Campus', phase: 'Commission', pct: 88, risk: 'HIGH', daysToSLA: 34 },
    { name: 'Peachtree Edge Data', phase: 'Build 70%', pct: 70, risk: 'HIGH', daysToSLA: null },
    { name: 'Sonoran Compute Park', phase: 'Site', pct: 22, risk: 'CRITICAL', daysToSLA: null },
    { name: 'Midwest Sovereign Exchange', phase: 'Site', pct: 18, risk: 'CRITICAL', daysToSLA: null },
    { name: 'Mile High AI Hub', phase: 'Site', pct: 15, risk: 'LOW', daysToSLA: null },
  ];

  const sevColor = (s) => s === 'CRITICAL' ? 'text-red-400 bg-red-500/12 border-red-500/25' : s === 'HIGH' ? 'text-amber-400 bg-amber-500/12 border-amber-500/25' : 'text-slate-400 bg-slate-700/40 border-slate-600/30';

  return (
    <div className="space-y-6">
      {/* Risk timeline — the "decisions that compound" */}
      <div>
        <div className="flex items-center gap-2 mb-4">
          <Clock className="w-4 h-4 text-orange-400" />
          <span className="text-sm font-bold text-white">Decision Timeline — Risks Before They Compound</span>
          <span className="text-[10px] text-slate-500 ml-auto font-mono">Cost of inaction scales 4× after steel is set</span>
        </div>
        <div className="bg-slate-800/40 border border-slate-700/40 rounded-2xl overflow-hidden">
          {/* Header */}
          <div className="grid grid-cols-12 px-4 py-2 border-b border-slate-700/40 text-[9px] font-bold uppercase tracking-widest text-slate-500">
            <span className="col-span-1">Age</span>
            <span className="col-span-3">Risk Signal</span>
            <span className="col-span-3">Project</span>
            <span className="col-span-2">Policy</span>
            <span className="col-span-2">Severity</span>
            <span className="col-span-1">→</span>
          </div>
          {riskItems.map((item, i) => (
            <motion.div key={i}
              className="grid grid-cols-12 px-4 py-3 border-b border-slate-700/20 hover:bg-slate-700/20 cursor-pointer transition-all items-center group"
              initial={{ opacity:0, x:-8 }} animate={{ opacity:1, x:0 }} transition={{ delay: i * 0.04 }}
              onClick={() => onPolicyClick(POLICIES.find(p => p.id === item.policy))}
            >
              <div className="col-span-1">
                <span className="text-[9px] font-mono text-slate-600">{item.days === 0 ? 'today' : `${item.days}d`}</span>
              </div>
              <div className="col-span-3 flex items-center gap-2">
                {item.severity === 'CRITICAL' && <div className="w-1.5 h-1.5 rounded-full bg-red-400 animate-pulse flex-shrink-0" />}
                {item.severity === 'HIGH' && <div className="w-1.5 h-1.5 rounded-full bg-amber-400 flex-shrink-0" />}
                {item.severity === 'MEDIUM' && <div className="w-1.5 h-1.5 rounded-full bg-slate-500 flex-shrink-0" />}
                <span className="text-xs text-white font-medium">{item.label}</span>
              </div>
              <div className="col-span-3">
                <span className="text-xs text-slate-400">{item.project}</span>
              </div>
              <div className="col-span-2">
                <span className="text-[9px] font-mono text-slate-500">{item.policy}</span>
              </div>
              <div className="col-span-2">
                <span className={`text-[9px] font-bold px-2 py-0.5 rounded-full border ${sevColor(item.severity)}`}>{item.severity}</span>
              </div>
              <div className="col-span-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <ChevronRight className="w-3 h-3 text-slate-500" />
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Project execution status */}
      <div>
        <div className="flex items-center gap-2 mb-4">
          <Activity className="w-4 h-4 text-orange-400" />
          <span className="text-sm font-bold text-white">Project Execution Status</span>
        </div>
        <div className="space-y-3">
          {projects.map((proj, i) => {
            const riskCls = proj.risk === 'CRITICAL' ? 'text-red-400' : proj.risk === 'HIGH' ? 'text-amber-400' : 'text-emerald-400';
            const barColor = proj.risk === 'CRITICAL' ? '#ef4444' : proj.risk === 'HIGH' ? '#f59e0b' : '#10b981';
            return (
              <motion.div key={proj.name}
                className="bg-slate-800/50 border border-slate-700/40 rounded-xl px-4 py-3"
                initial={{ opacity:0, y:6 }} animate={{ opacity:1, y:0 }} transition={{ delay: i * 0.07 }}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-semibold text-white">{proj.name}</span>
                    <PhasePill phase={proj.phase.split(' ')[0]} />
                    <span className="text-[10px] text-slate-500">{proj.phase}</span>
                  </div>
                  <div className="flex items-center gap-4">
                    {proj.daysToSLA && (
                      <span className="text-[10px] text-red-400 font-mono font-bold">{proj.daysToSLA}d to SLA</span>
                    )}
                    <span className={`text-[10px] font-bold ${riskCls}`}>{proj.risk}</span>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex-1 h-1.5 bg-slate-700 rounded-full overflow-hidden">
                    <motion.div className="h-full rounded-full" style={{ background: barColor }}
                      initial={{ width: 0 }} animate={{ width: `${proj.pct}%` }} transition={{ duration: 0.8, delay: i * 0.07 + 0.2 }}
                    />
                  </div>
                  <span className="text-[10px] font-mono text-slate-500 w-8 text-right">{proj.pct}%</span>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* Key Build + Commission policies */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <Database className="w-4 h-4 text-orange-400" />
          <span className="text-sm font-bold text-white">Active Build + Commission Policies</span>
        </div>
        <div className="grid grid-cols-2 gap-3">
          {buildPolicies.map((p, i) => (
            <motion.div key={p.id} initial={{ opacity:0 }} animate={{ opacity:1 }} transition={{ delay: i * 0.04 }}>
              <PolicyMicro policy={p} onClick={onPolicyClick} />
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// VIEW 3 — FUND: Exit optimization & portfolio benchmarking
// ═══════════════════════════════════════════════════════════════════════════

const FundView = ({ onPolicyClick }) => {
  const capPolicies = POLICIES.filter(p => ['Capital','Exit'].includes(p.category));

  const assets = [
    { name: 'Virginia Hyperscale Cluster', mw: 400, pue: 1.42, pueBench: 1.36, govScore: 74, exitScore: 82, buyers: 3, status: 'EXIT_READY' },
    { name: 'Lone Star AI Campus', mw: 250, pue: 1.38, pueBench: 1.36, govScore: 58, exitScore: 41, buyers: 0, status: 'BUILD' },
    { name: 'Sonoran Compute Park', mw: 180, pue: null, pueBench: 1.36, govScore: null, exitScore: null, buyers: 0, status: 'SITE' },
  ];

  const transactions = [
    { name: 'Switch REIT IPO', year: 2017, multiple: '11×', premium: '+18%', type: 'Peak cycle', color: '#a78bfa' },
    { name: 'QTS Blackstone', year: 2021, multiple: '28×', premium: '+24%', type: 'AI demand', color: '#818cf8' },
    { name: 'Aligned Blackrock', year: 2025, multiple: '—', premium: '+22%', type: 'Sovereign AI', color: '#6366f1' },
  ];

  const statusColor = (s) => s === 'EXIT_READY' ? 'text-emerald-400 bg-emerald-500/10 border-emerald-500/25' : s === 'BUILD' ? 'text-amber-400 bg-amber-500/10 border-amber-500/25' : 'text-slate-400 bg-slate-700/20 border-slate-600/20';

  return (
    <div className="space-y-6">
      {/* Exit window signal — hero card */}
      <div className="bg-gradient-to-br from-violet-950/80 to-indigo-950/60 border border-violet-500/30 rounded-2xl p-5">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-violet-400 mb-1">CPTL-001 · Exit Window Optimization</div>
            <div className="text-lg font-bold text-white">Optimal Exit Window — Virginia Hyperscale Cluster</div>
            <div className="text-sm text-slate-300 mt-1">Demand cycle model: <span className="text-emerald-400 font-bold">85/100</span> · Governance readiness: <span className="text-amber-400 font-bold">74/100</span></div>
          </div>
          <div className="text-right flex-shrink-0">
            <div className="text-3xl font-bold text-emerald-400">$180M+</div>
            <div className="text-[10px] text-slate-500 uppercase tracking-wider">timing uplift modeled</div>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-3">
          {[
            { label: 'Buyers identified', val: '3 active mandates', color: 'text-cyan-400' },
            { label: 'Window opens', val: 'Q3 2026', color: 'text-emerald-400' },
            { label: 'PUE optimization needed', val: '0.06 improvement → +2.1%', color: 'text-violet-300' },
          ].map(({ label, val, color }) => (
            <div key={label} className="bg-slate-900/40 rounded-xl p-3">
              <div className="text-[9px] text-slate-600 uppercase tracking-wider mb-1">{label}</div>
              <div className={`text-xs font-semibold ${color}`}>{val}</div>
            </div>
          ))}
        </div>
        <button onClick={() => onPolicyClick(POLICIES.find(p => p.id === 'CPTL-001'))}
          className="mt-3 text-[10px] text-violet-400 hover:text-violet-300 font-semibold flex items-center gap-1 transition-colors">
          View policy detail <ChevronRight className="w-3 h-3" />
        </button>
      </div>

      {/* Portfolio asset table */}
      <div>
        <div className="flex items-center gap-2 mb-4">
          <BarChart2 className="w-4 h-4 text-violet-400" />
          <span className="text-sm font-bold text-white">Portfolio Asset Readiness</span>
        </div>
        <div className="bg-slate-800/40 border border-slate-700/40 rounded-2xl overflow-hidden">
          <div className="grid grid-cols-12 px-5 py-2.5 border-b border-slate-700/40 text-[9px] font-bold uppercase tracking-widest text-slate-500">
            <span className="col-span-3">Asset</span>
            <span className="col-span-1 text-center">MW</span>
            <span className="col-span-2 text-center">PUE vs Bench</span>
            <span className="col-span-2 text-center">Gov Score</span>
            <span className="col-span-2 text-center">Exit Score</span>
            <span className="col-span-2 text-right">Status</span>
          </div>
          {assets.map((a, i) => (
            <motion.div key={a.name}
              className="grid grid-cols-12 px-5 py-4 border-b border-slate-700/20 items-center"
              initial={{ opacity:0 }} animate={{ opacity:1 }} transition={{ delay: i * 0.08 }}
            >
              <div className="col-span-3 text-sm font-semibold text-white leading-tight">{a.name}</div>
              <div className="col-span-1 text-center font-mono text-sm text-slate-300">{a.mw}</div>
              <div className="col-span-2 text-center">
                {a.pue ? (
                  <div>
                    <span className={`text-xs font-bold ${a.pue > a.pueBench ? 'text-amber-400' : 'text-emerald-400'}`}>{a.pue}</span>
                    <span className="text-[9px] text-slate-600"> vs {a.pueBench}</span>
                  </div>
                ) : <span className="text-slate-600 text-xs">—</span>}
              </div>
              <div className="col-span-2 text-center">
                {a.govScore ? (
                  <div className="flex items-center justify-center gap-2">
                    <div className="w-12 h-1 bg-slate-700 rounded-full overflow-hidden">
                      <div className="h-full rounded-full bg-indigo-500" style={{ width:`${a.govScore}%` }} />
                    </div>
                    <span className={`text-xs font-bold ${a.govScore >= 80 ? 'text-emerald-400' : 'text-amber-400'}`}>{a.govScore}</span>
                  </div>
                ) : <span className="text-slate-600 text-xs">—</span>}
              </div>
              <div className="col-span-2 text-center">
                {a.exitScore ? (
                  <span className={`text-lg font-bold ${a.exitScore >= 80 ? 'text-emerald-400' : a.exitScore >= 50 ? 'text-amber-400' : 'text-red-400'}`}>{a.exitScore}</span>
                ) : <span className="text-slate-600 text-xs">—</span>}
              </div>
              <div className="col-span-2 text-right">
                <span className={`text-[9px] font-bold px-2 py-0.5 rounded-full border ${statusColor(a.status)}`}>{a.status.replace('_',' ')}</span>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Comparable transactions */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <TrendingUp className="w-4 h-4 text-violet-400" />
          <span className="text-sm font-bold text-white">Corpus Transaction Comps — Timing Matters</span>
        </div>
        <div className="grid grid-cols-3 gap-3">
          {transactions.map((t, i) => (
            <motion.div key={t.name}
              className="bg-slate-800/50 border border-slate-700/40 rounded-xl p-4"
              initial={{ opacity:0, y:8 }} animate={{ opacity:1, y:0 }} transition={{ delay: i * 0.08 }}
            >
              <div className="text-[9px] text-slate-600 mb-1">{t.year} · {t.type}</div>
              <div className="text-xs font-bold text-white mb-2 leading-tight">{t.name}</div>
              <div className="text-2xl font-bold mb-1" style={{ color: t.color }}>{t.multiple}</div>
              <div className="text-[10px] font-semibold text-emerald-400">EBITDA{' '}{t.premium} vs off-cycle</div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Capital + Exit policies */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <Database className="w-4 h-4 text-violet-400" />
          <span className="text-sm font-bold text-white">Capital + Exit Policies</span>
        </div>
        <div className="grid grid-cols-2 gap-3">
          {capPolicies.map((p, i) => (
            <motion.div key={p.id} initial={{ opacity:0 }} animate={{ opacity:1 }} transition={{ delay: i * 0.05 }}>
              <PolicyMicro policy={p} onClick={onPolicyClick} />
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// VIEW 4 — HYPERSCALER: Campus delivery & SLA protection
// ═══════════════════════════════════════════════════════════════════════════

const HyperscalerView = ({ onPolicyClick }) => {
  const relPolicies = POLICIES.filter(p => ['Build','Commission'].includes(p.category));
  const triggered = relPolicies.filter(p => p.status === 'Triggered');

  const campuses = [
    { name: 'Lone Star AI Campus', mw: 250, gpus: 9600, commissioning: 88, daysToSLA: 34, slaRisk: 'HIGH', phase: 'IST', triggers: ['COMM-001','COMM-002'] },
    { name: 'Peachtree Edge Data', mw: 80, gpus: 3200, commissioning: 0, daysToSLA: null, slaRisk: 'MEDIUM', phase: 'Build 70%', triggers: ['BILD-001'] },
  ];

  const supplyChain = [
    { item: 'Vertiv UPS Units', vendor: 'Vertiv', status: 'DELAYED', deviation: '+14 wks', sites: 3, policy: 'BILD-001' },
    { item: 'Liebert CRAC', vendor: 'Liebert', status: 'AT RISK', deviation: '+6 wks', sites: 2, policy: 'BILD-001' },
    { item: 'Generac Generators', vendor: 'Generac', status: 'DELAYED', deviation: '+10 wks', sites: 2, policy: 'BILD-002' },
    { item: 'GE Switchgear', vendor: 'GE Grid', status: 'ON TRACK', deviation: '—', sites: 4, policy: null },
  ];

  const scColor = (s) => s === 'DELAYED' ? 'text-red-400 bg-red-500/10 border-red-500/20' : s === 'AT RISK' ? 'text-amber-400 bg-amber-500/10 border-amber-500/20' : 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20';

  return (
    <div className="space-y-6">
      {/* Campus delivery dashboard */}
      <div>
        <div className="flex items-center gap-2 mb-4">
          <Factory className="w-4 h-4 text-cyan-400" />
          <span className="text-sm font-bold text-white">Gigawatt Campus Delivery Status</span>
        </div>
        <div className="grid grid-cols-2 gap-4">
          {campuses.map((campus, i) => (
            <motion.div key={campus.name}
              className={`bg-slate-800/50 border rounded-2xl p-5 ${campus.slaRisk === 'HIGH' ? 'border-amber-500/30' : 'border-slate-700/40'}`}
              initial={{ opacity:0, y:8 }} animate={{ opacity:1, y:0 }} transition={{ delay: i * 0.1 }}
            >
              <div className="flex items-start justify-between mb-3">
                <div>
                  <div className="text-sm font-bold text-white">{campus.name}</div>
                  <div className="text-[10px] text-slate-500 mt-0.5">{campus.mw}MW · {campus.gpus.toLocaleString()} GPUs</div>
                </div>
                {campus.daysToSLA && (
                  <div className="text-right">
                    <div className="text-xl font-bold text-amber-400">{campus.daysToSLA}</div>
                    <div className="text-[9px] text-slate-600 uppercase tracking-wider">days to SLA</div>
                  </div>
                )}
              </div>

              {campus.commissioning > 0 && (
                <div className="mb-3">
                  <div className="flex justify-between text-[9px] text-slate-500 mb-1.5">
                    <span>Commission progress</span>
                    <span className="font-mono">{campus.commissioning}%</span>
                  </div>
                  <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                    <motion.div className="h-full rounded-full bg-gradient-to-r from-cyan-500 to-sky-400"
                      initial={{ width:0 }} animate={{ width:`${campus.commissioning}%` }} transition={{ duration:0.9, delay: i * 0.1 + 0.3 }}
                    />
                  </div>
                </div>
              )}

              <div className="flex items-center gap-2 flex-wrap">
                <PhasePill phase={campus.phase.split(' ')[0]} />
                {campus.triggers.map(t => (
                  <button key={t} onClick={() => onPolicyClick(POLICIES.find(p => p.id === t))}
                    className="text-[9px] font-mono text-red-400 bg-red-500/10 border border-red-500/20 px-2 py-0.5 rounded hover:bg-red-500/15 transition-colors">
                    {t} ▲
                  </button>
                ))}
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Supply chain board — BILD-001/002 */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <Network className="w-4 h-4 text-cyan-400" />
          <span className="text-sm font-bold text-white">Critical Path Equipment · Supply Chain Monitor</span>
          <span className="text-[10px] text-slate-500 ml-auto">Corpus: 47 build phases · 12 equipment categories</span>
        </div>
        <div className="bg-slate-800/40 border border-slate-700/40 rounded-2xl overflow-hidden">
          <div className="grid grid-cols-12 px-5 py-2.5 border-b border-slate-700/40 text-[9px] font-bold uppercase tracking-widest text-slate-500">
            <span className="col-span-3">Equipment</span>
            <span className="col-span-2">Vendor</span>
            <span className="col-span-2 text-center">Status</span>
            <span className="col-span-2 text-center">Deviation</span>
            <span className="col-span-1 text-center">Sites</span>
            <span className="col-span-2 text-center">Policy</span>
          </div>
          {supplyChain.map((item, i) => (
            <motion.div key={item.item}
              className="grid grid-cols-12 px-5 py-3.5 border-b border-slate-700/20 hover:bg-slate-700/20 cursor-pointer items-center group"
              initial={{ opacity:0 }} animate={{ opacity:1 }} transition={{ delay: i * 0.06 }}
              onClick={() => item.policy && onPolicyClick(POLICIES.find(p => p.id === item.policy))}
            >
              <div className="col-span-3 text-xs font-semibold text-white">{item.item}</div>
              <div className="col-span-2 text-xs text-slate-400">{item.vendor}</div>
              <div className="col-span-2 flex justify-center">
                <span className={`text-[9px] font-bold px-2 py-0.5 rounded-full border ${scColor(item.status)}`}>{item.status}</span>
              </div>
              <div className="col-span-2 text-center">
                <span className={`text-xs font-mono font-bold ${item.deviation !== '—' ? 'text-red-400' : 'text-emerald-400'}`}>{item.deviation}</span>
              </div>
              <div className="col-span-1 text-center">
                <span className="text-xs font-bold text-slate-300">{item.sites}</span>
              </div>
              <div className="col-span-2 flex justify-center">
                {item.policy ? (
                  <span className="text-[9px] font-mono text-amber-400 bg-amber-500/10 border border-amber-500/20 px-2 py-0.5 rounded group-hover:bg-amber-500/15 transition-colors">{item.policy}</span>
                ) : <span className="text-slate-700 text-[9px]">—</span>}
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Commission policies */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <Database className="w-4 h-4 text-cyan-400" />
          <span className="text-sm font-bold text-white">Build + Commission Policies</span>
        </div>
        <div className="grid grid-cols-2 gap-3">
          {relPolicies.map((p, i) => (
            <motion.div key={p.id} initial={{ opacity:0 }} animate={{ opacity:1 }} transition={{ delay: i * 0.04 }}>
              <PolicyMicro policy={p} onClick={onPolicyClick} />
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// VIEW 5 — LENDER + INSURER: Signal-based risk underwriting
// ═══════════════════════════════════════════════════════════════════════════

const LenderView = ({ onPolicyClick }) => {
  const relPolicies = POLICIES.filter(p => ['Build','Commission','Capital'].includes(p.category));

  const riskProfiles = [
    { name: 'Lone Star AI Campus', loan: '$420M', ltv: '62%', completionConf: 71, budgetVar: '+1.8%', constructionRisk: 'HIGH', triggers: ['COMM-001','COMM-002'], clauseRisk: true },
    { name: 'Peachtree Edge Data', loan: '$180M', ltv: '58%', completionConf: 81, budgetVar: '+1.1%', constructionRisk: 'MEDIUM', triggers: ['BILD-001'], clauseRisk: false },
    { name: 'Midwest Sovereign Exchange', loan: '$240M', ltv: '61%', completionConf: 84, budgetVar: '+0.4%', constructionRisk: 'LOW', triggers: [], clauseRisk: false },
  ];

  const riskColor = (r) => r === 'HIGH' ? 'text-red-400 bg-red-500/10 border-red-500/25' : r === 'MEDIUM' ? 'text-amber-400 bg-amber-500/10 border-amber-500/25' : 'text-emerald-400 bg-emerald-500/10 border-emerald-500/25';

  const clauseEvents = [
    { policy: 'COMM-001', project: 'Lone Star AI Campus', clause: 'Construction completion clause §4.2', severity: 'HIGH', risk: '$18M penalty exposure' },
    { policy: 'BILD-001', project: 'Peachtree Edge Data', clause: 'Equipment delivery clause §3.1', severity: 'MEDIUM', risk: '$6M delay exposure' },
    { policy: 'CPTL-003', project: 'Lone Star AI Campus', clause: 'Budget variance covenant', severity: 'MEDIUM', risk: 'Lender consent required' },
  ];

  return (
    <div className="space-y-6">
      {/* Hero — the anti-spreadsheet pitch */}
      <div className="bg-gradient-to-br from-emerald-950/60 to-slate-900/60 border border-emerald-500/25 rounded-2xl p-5">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-9 h-9 bg-emerald-500/15 rounded-xl flex items-center justify-center border border-emerald-500/25">
            <Database className="w-4.5 h-4.5 text-emerald-400" />
          </div>
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-emerald-400">Signal-Based Risk Underwriting</div>
            <div className="text-xs text-slate-400 mt-0.5">Replacing spreadsheet snapshots with 52-project governed corpus</div>
          </div>
        </div>
        <div className="grid grid-cols-4 gap-4">
          {[
            { label: 'Projects in corpus', val: '52', color: 'text-white' },
            { label: 'Policies monitoring', val: '16', color: 'text-emerald-400' },
            { label: 'Commission risk confidence', val: '91%', color: 'text-cyan-400' },
            { label: 'Value underwritten', val: '$840M+', color: 'text-indigo-300' },
          ].map(({ label, val, color }) => (
            <div key={label} className="bg-slate-900/50 rounded-xl p-3 text-center">
              <div className={`text-xl font-bold ${color}`}>{val}</div>
              <div className="text-[9px] text-slate-600 uppercase tracking-wider mt-1">{label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Loan portfolio risk table */}
      <div>
        <div className="flex items-center gap-2 mb-4">
          <Shield className="w-4 h-4 text-emerald-400" />
          <span className="text-sm font-bold text-white">Portfolio Risk Assessment · Signal-Based</span>
        </div>
        <div className="space-y-3">
          {riskProfiles.map((loan, i) => (
            <motion.div key={loan.name}
              className={`bg-slate-800/50 border rounded-2xl p-5 ${loan.constructionRisk === 'HIGH' ? 'border-red-500/25' : 'border-slate-700/40'}`}
              initial={{ opacity:0, y:6 }} animate={{ opacity:1, y:0 }} transition={{ delay: i * 0.08 }}
            >
              <div className="flex items-start justify-between mb-3">
                <div>
                  <div className="text-sm font-bold text-white">{loan.name}</div>
                  <div className="text-[10px] text-slate-500 mt-0.5 font-mono">{loan.loan} construction loan · LTV {loan.ltv}</div>
                </div>
                <span className={`text-[9px] font-bold px-2 py-0.5 rounded-full border ${riskColor(loan.constructionRisk)}`}>{loan.constructionRisk} RISK</span>
              </div>

              <div className="grid grid-cols-3 gap-3 mb-3">
                <div className="bg-slate-900/40 rounded-xl p-3 text-center">
                  <div className="text-[9px] text-slate-600 mb-1">Completion Confidence</div>
                  <div className={`text-lg font-bold ${loan.completionConf >= 80 ? 'text-emerald-400' : loan.completionConf >= 70 ? 'text-amber-400' : 'text-red-400'}`}>{loan.completionConf}%</div>
                </div>
                <div className="bg-slate-900/40 rounded-xl p-3 text-center">
                  <div className="text-[9px] text-slate-600 mb-1">Budget Variance</div>
                  <div className={`text-lg font-bold ${parseFloat(loan.budgetVar) > 2 ? 'text-amber-400' : 'text-emerald-400'}`}>{loan.budgetVar}</div>
                </div>
                <div className="bg-slate-900/40 rounded-xl p-3 text-center">
                  <div className="text-[9px] text-slate-600 mb-1">Active Triggers</div>
                  <div className={`text-lg font-bold ${loan.triggers.length > 0 ? 'text-red-400' : 'text-emerald-400'}`}>{loan.triggers.length}</div>
                </div>
              </div>

              {loan.triggers.length > 0 && (
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[9px] text-red-400/70 uppercase tracking-wider">Triggered:</span>
                  {loan.triggers.map(t => (
                    <button key={t} onClick={() => onPolicyClick(POLICIES.find(p => p.id === t))}
                      className="text-[9px] font-mono text-red-400 bg-red-500/10 border border-red-500/20 px-2 py-0.5 rounded hover:bg-red-500/15 transition-colors">
                      {t} →
                    </button>
                  ))}
                  {loan.clauseRisk && <span className="text-[9px] text-amber-400 bg-amber-500/10 border border-amber-500/20 px-2 py-0.5 rounded">Clause exposure active</span>}
                </div>
              )}
            </motion.div>
          ))}
        </div>
      </div>

      {/* Clause trigger events */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <AlertCircle className="w-4 h-4 text-emerald-400" />
          <span className="text-sm font-bold text-white">Contract Clause Signals — Policy-Triggered</span>
        </div>
        <div className="space-y-2">
          {clauseEvents.map((event, i) => (
            <motion.div key={i}
              className={`flex items-center gap-4 bg-slate-800/50 border rounded-xl px-4 py-3 cursor-pointer hover:bg-slate-800/80 transition-all ${event.severity === 'HIGH' ? 'border-red-500/20' : 'border-slate-700/40'}`}
              initial={{ opacity:0 }} animate={{ opacity:1 }} transition={{ delay: i * 0.07 }}
              onClick={() => onPolicyClick(POLICIES.find(p => p.id === event.policy))}
            >
              <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${event.severity === 'HIGH' ? 'bg-red-400 animate-pulse' : 'bg-amber-400'}`} />
              <div className="flex-1 min-w-0">
                <div className="text-xs font-semibold text-white mb-0.5">{event.clause}</div>
                <div className="text-[10px] text-slate-500">{event.project}</div>
              </div>
              <div className="text-right flex-shrink-0">
                <div className="text-[9px] text-slate-600">Exposure</div>
                <div className="text-xs font-bold text-amber-400">{event.risk}</div>
              </div>
              <span className="text-[9px] font-mono text-slate-600 flex-shrink-0">{event.policy}</span>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Relevant policies */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <Database className="w-4 h-4 text-emerald-400" />
          <span className="text-sm font-bold text-white">Build · Commission · Capital Policies</span>
        </div>
        <div className="grid grid-cols-2 gap-3">
          {relPolicies.map((p, i) => (
            <motion.div key={p.id} initial={{ opacity:0 }} animate={{ opacity:1 }} transition={{ delay: i * 0.04 }}>
              <PolicyMicro policy={p} onClick={onPolicyClick} />
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// VIEW 6 — GOVERNMENT: Standardized infrastructure assessment
// ═══════════════════════════════════════════════════════════════════════════

const GovernmentView = ({ onPolicyClick }) => {
  const relPolicies = POLICIES.filter(p => ['Site','Build','Commission'].includes(p.category));

  const jurisdictions = [
    { name: 'Maricopa County, AZ', projects: 2, permScore: 58, gridScore: 72, waterScore: 65, overall: 65, trend: -3 },
    { name: 'Dallas County, TX', projects: 2, permScore: 81, gridScore: 88, waterScore: 84, overall: 84, trend: +5 },
    { name: 'Denver Metro, CO', projects: 1, permScore: 74, gridScore: 79, waterScore: 61, overall: 71, trend: +1 },
    { name: 'Chicago Metro, IL', projects: 1, permScore: 69, gridScore: 85, waterScore: 90, overall: 81, trend: +8 },
  ];

  const scoreColor = (s) => s >= 80 ? 'text-emerald-400' : s >= 65 ? 'text-amber-400' : 'text-red-400';
  const barColor = (s) => s >= 80 ? '#10b981' : s >= 65 ? '#f59e0b' : '#ef4444';

  const readinessCategories = [
    { label: 'Grid capacity & queue transparency', score: 68, policyId: 'SITE-001' },
    { label: 'Permitting efficiency & timeline', score: 62, policyId: 'SITE-004' },
    { label: 'Water rights frameworks', score: 71, policyId: 'SITE-003' },
    { label: 'Fiber & connectivity infrastructure', score: 75, policyId: 'SITE-002' },
    { label: 'Construction workforce availability', score: 79, policyId: 'BILD-004' },
    { label: 'Commissioning standard alignment', score: 84, policyId: 'COMM-003' },
  ];

  return (
    <div className="space-y-6">
      {/* Public interest framing */}
      <div className="bg-gradient-to-br from-rose-950/60 to-slate-900/60 border border-rose-500/25 rounded-2xl p-5">
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 bg-rose-500/15 rounded-xl flex items-center justify-center border border-rose-500/25 flex-shrink-0">
            <Globe className="w-5 h-5 text-rose-400" />
          </div>
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-rose-400 mb-1">Infrastructure Readiness Assessment</div>
            <div className="text-sm font-bold text-white mb-2">Standardized quality views over institutional memory — not spreadsheet snapshots</div>
            <div className="text-xs text-slate-400 leading-relaxed">Axiom's 16-policy engine generates the first standardized AI infrastructure quality index — trained on 38 jurisdictions, 52 projects, $5B of actual decisions. Governments and economic development agencies can assess readiness without depending on developer-supplied data.</div>
          </div>
        </div>
      </div>

      {/* Jurisdiction readiness index */}
      <div>
        <div className="flex items-center gap-2 mb-4">
          <Globe className="w-4 h-4 text-rose-400" />
          <span className="text-sm font-bold text-white">Jurisdiction Readiness Index · Corpus-Calibrated</span>
        </div>
        <div className="bg-slate-800/40 border border-slate-700/40 rounded-2xl overflow-hidden">
          <div className="grid grid-cols-12 px-5 py-2.5 border-b border-slate-700/40 text-[9px] font-bold uppercase tracking-widest text-slate-500">
            <span className="col-span-3">Jurisdiction</span>
            <span className="col-span-1 text-center">Projects</span>
            <span className="col-span-2 text-center">Permitting</span>
            <span className="col-span-2 text-center">Grid</span>
            <span className="col-span-2 text-center">Water</span>
            <span className="col-span-2 text-right">Overall</span>
          </div>
          {jurisdictions.map((j, i) => (
            <motion.div key={j.name}
              className="grid grid-cols-12 px-5 py-4 border-b border-slate-700/20 items-center"
              initial={{ opacity:0 }} animate={{ opacity:1 }} transition={{ delay: i * 0.08 }}
            >
              <div className="col-span-3 text-xs font-semibold text-white">{j.name}</div>
              <div className="col-span-1 text-center text-xs text-slate-400">{j.projects}</div>
              {[j.permScore, j.gridScore, j.waterScore].map((score, si) => (
                <div key={si} className="col-span-2 flex items-center justify-center gap-2">
                  <div className="w-10 h-1 bg-slate-700 rounded-full overflow-hidden">
                    <div className="h-full rounded-full" style={{ width:`${score}%`, background: barColor(score) }} />
                  </div>
                  <span className={`text-xs font-bold ${scoreColor(score)}`}>{score}</span>
                </div>
              ))}
              <div className="col-span-2 flex items-center justify-end gap-2">
                <span className={`text-base font-bold ${scoreColor(j.overall)}`}>{j.overall}</span>
                <span className={`text-[9px] font-bold ${j.trend > 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                  {j.trend > 0 ? '+' : ''}{j.trend}
                </span>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Infrastructure readiness categories */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <Target className="w-4 h-4 text-rose-400" />
          <span className="text-sm font-bold text-white">AI Infrastructure Readiness Scorecard · National Average</span>
        </div>
        <div className="space-y-2.5">
          {readinessCategories.map((cat, i) => (
            <motion.div key={cat.label}
              className="bg-slate-800/50 border border-slate-700/40 rounded-xl px-4 py-3 cursor-pointer hover:bg-slate-800/80 transition-all"
              initial={{ opacity:0, x:-8 }} animate={{ opacity:1, x:0 }} transition={{ delay: i * 0.06 }}
              onClick={() => onPolicyClick(POLICIES.find(p => p.id === cat.policyId))}
            >
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-semibold text-white">{cat.label}</span>
                <div className="flex items-center gap-3">
                  <span className={`text-sm font-bold ${scoreColor(cat.score)}`}>{cat.score}/100</span>
                  <span className="text-[9px] font-mono text-slate-600">{cat.policyId}</span>
                </div>
              </div>
              <div className="h-1.5 bg-slate-700 rounded-full overflow-hidden">
                <motion.div className="h-full rounded-full"
                  style={{ background: barColor(cat.score) }}
                  initial={{ width: 0 }} animate={{ width: `${cat.score}%` }}
                  transition={{ duration: 0.8, delay: i * 0.06 + 0.2 }}
                />
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Site policies from public-interest lens */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <Database className="w-4 h-4 text-rose-400" />
          <span className="text-sm font-bold text-white">Site + Build + Commission Policies · Public-Interest Lens</span>
        </div>
        <div className="grid grid-cols-2 gap-3">
          {relPolicies.slice(0, 8).map((p, i) => (
            <motion.div key={p.id} initial={{ opacity:0 }} animate={{ opacity:1 }} transition={{ delay: i * 0.04 }}>
              <PolicyMicro policy={p} onClick={onPolicyClick} />
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// VIEW ROUTER
// ═══════════════════════════════════════════════════════════════════════════

const VIEW_MAP = {
  owners_rep:  OwnersRepView,
  developer:   DeveloperView,
  fund:        FundView,
  hyperscaler: HyperscalerView,
  lender:      LenderView,
  government:  GovernmentView,
};

// ═══════════════════════════════════════════════════════════════════════════
// MAIN — AxiomStakeholder
// ═══════════════════════════════════════════════════════════════════════════

const AxiomStakeholder = () => {
  const [activeId, setActiveId] = useState('owners_rep');
  const [selectedPolicy, setSelectedPolicy] = useState(null);
  const active = STAKEHOLDERS.find(s => s.id === activeId);
  const ActiveView = VIEW_MAP[activeId];

  const triggeredCount = POLICIES.filter(p => p.status === 'Triggered').length;

  return (
    <div className="min-h-screen w-full bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 relative">
      {/* Dot grid background */}
      <div className="fixed inset-0 opacity-[0.07] pointer-events-none" style={{
        backgroundImage: `radial-gradient(circle at 1px 1px, rgba(99,102,241,0.6) 1px, transparent 0)`,
        backgroundSize: '32px 32px',
      }} />

      {/* ── HERO HEADER ── */}
      <div className="relative z-10 px-6 pt-8 pb-0">
        {/* Axiom brand row */}
        <div className="flex items-start justify-between gap-6 mb-8">
          <div>
            {/* Eyebrow */}
            <motion.div
              initial={{ opacity:0, y:-8 }} animate={{ opacity:1, y:0 }} transition={{ duration:0.4 }}
              className="flex items-center gap-2 mb-3"
            >
              <div className="w-8 h-8 bg-gradient-to-br from-indigo-500 to-violet-600 rounded-xl flex items-center justify-center">
                <Brain className="w-4 h-4 text-white" />
              </div>
              <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-indigo-400">Axiom Intelligence Platform</span>
            </motion.div>

            <motion.h1
              initial={{ opacity:0, y:-4 }} animate={{ opacity:1, y:0 }} transition={{ duration:0.4, delay:0.05 }}
              className="text-3xl font-bold text-white leading-tight mb-2"
            >
              One corpus.{' '}
              <span className="text-transparent bg-clip-text"
                style={{ backgroundImage: `linear-gradient(135deg, ${active.accent.from}, ${active.accent.to})` }}>
                Six stakeholders.
              </span>
              {' '}Every deal.
            </motion.h1>

            <motion.p
              initial={{ opacity:0 }} animate={{ opacity:1 }} transition={{ duration:0.4, delay:0.1 }}
              className="text-sm text-slate-400 max-w-2xl leading-relaxed"
            >
              CoStar became the authoritative data platform for commercial real estate without managing a single property.
              This is the same play for AI infrastructure — 16 governed policies, $5B corpus basis, compounding network effects with every build.
            </motion.p>
          </div>

          {/* Live corpus stats */}
          <motion.div
            initial={{ opacity:0, x:8 }} animate={{ opacity:1, x:0 }} transition={{ duration:0.4, delay:0.15 }}
            className="flex-shrink-0 bg-slate-800/60 border border-slate-700/50 rounded-2xl p-4 min-w-[280px]"
          >
            <div className="text-[9px] font-bold uppercase tracking-[0.2em] text-slate-500 mb-3">Corpus Status · Live</div>
            <div className="grid grid-cols-3 gap-3">
              {[
                { val: 16, label: 'Policies', color: 'text-indigo-300' },
                { val: triggeredCount, label: 'Triggered', color: triggeredCount > 0 ? 'text-red-400' : 'text-emerald-400' },
                { val: '$280M+', label: 'Protected', color: 'text-emerald-400' },
              ].map(({ val, label, color }) => (
                <div key={label} className="text-center">
                  <div className={`text-xl font-bold ${color}`}>{val}</div>
                  <div className="text-[9px] text-slate-600 uppercase tracking-wider mt-0.5">{label}</div>
                </div>
              ))}
            </div>
            {triggeredCount > 0 && (
              <div className="mt-3 flex items-center gap-1.5 bg-red-500/8 border border-red-500/20 rounded-xl px-3 py-2">
                <div className="w-1.5 h-1.5 rounded-full bg-red-400 animate-pulse flex-shrink-0" />
                <span className="text-[10px] text-red-300 font-medium">{triggeredCount} policies triggered — action required</span>
              </div>
            )}
          </motion.div>
        </div>

        {/* ── STAKEHOLDER SELECTOR ── */}
        <div className="mb-6">
          <div className="text-[9px] font-bold uppercase tracking-[0.2em] text-slate-600 mb-3">
            Same corpus · Different policy lens
          </div>
          <div className="flex gap-2 flex-wrap">
            {STAKEHOLDERS.map((s, i) => {
              const Icon = s.Icon;
              const isActive = s.id === activeId;
              return (
                <motion.button
                  key={s.id}
                  onClick={() => setActiveId(s.id)}
                  className={`flex items-center gap-2.5 px-4 py-2.5 rounded-xl border text-sm font-semibold transition-all ${
                    isActive
                      ? `bg-gradient-to-r ${s.activeBg} border-transparent text-white shadow-lg`
                      : 'bg-slate-800/60 border-slate-700/50 text-slate-400 hover:text-white hover:border-slate-600/70 hover:bg-slate-800'
                  }`}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.97 }}
                  initial={{ opacity:0, y:8 }}
                  animate={{ opacity:1, y:0 }}
                  transition={{ delay: i * 0.05 }}
                >
                  <Icon className={`w-4 h-4 ${isActive ? 'text-white' : 'text-slate-500'}`} />
                  {s.label}
                  {isActive && (
                    <div className="w-1.5 h-1.5 rounded-full bg-white/70 animate-pulse" />
                  )}
                </motion.button>
              );
            })}
          </div>
        </div>

        {/* ── ACTIVE STAKEHOLDER CONTEXT BAR ── */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activeId}
            className={`flex items-center gap-4 bg-slate-800/50 border ${active.borderAccent} rounded-2xl px-5 py-3.5 mb-6`}
            initial={{ opacity:0, y:4 }} animate={{ opacity:1, y:0 }} exit={{ opacity:0, y:-4 }}
            transition={{ duration:0.2 }}
          >
            <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background:`linear-gradient(135deg, ${active.accent.from}20, ${active.accent.to}20)`, border:`1px solid ${active.accent.from}30` }}>
              <active.Icon className="w-4.5 h-4.5" style={{ color: active.accent.from }} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-0.5">
                <span className="text-xs font-bold text-white">{active.label}</span>
                <span className="text-[9px]" style={{ color: active.accent.from }}>·</span>
                <span className="text-[10px] text-slate-400">{active.tagline}</span>
              </div>
              <div className="text-[10px] text-slate-500 leading-relaxed">{active.pitch}</div>
            </div>
            <div className="text-right flex-shrink-0">
              <div className={`text-lg font-bold ${active.heroStat.color}`}>{active.heroStat.num}</div>
              <div className="text-[9px] text-slate-600 uppercase tracking-wider">{active.heroStat.label}</div>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>

      {/* ── MAIN CONTENT — STAKEHOLDER VIEW ── */}
      <div className="relative z-10 px-6 pb-12">
        <AnimatePresence mode="wait">
          <motion.div
            key={activeId}
            initial={{ opacity:0, y:12 }}
            animate={{ opacity:1, y:0 }}
            exit={{ opacity:0, y:-8 }}
            transition={{ duration:0.25 }}
          >
            <ActiveView onPolicyClick={setSelectedPolicy} />
          </motion.div>
        </AnimatePresence>
      </div>

      {/* ── POLICY DETAIL DRAWER ── */}
      <AnimatePresence>
        {selectedPolicy && (
          <PolicyDrawer
            policy={selectedPolicy}
            onClose={() => setSelectedPolicy(null)}
            accentColor={active.accent.from}
          />
        )}
      </AnimatePresence>

      {/* ── FOOTER ── */}
      <div className="relative z-10 px-6 pb-8 border-t border-slate-800/60 pt-6 mt-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 bg-gradient-to-br from-indigo-500 to-violet-600 rounded-md flex items-center justify-center">
              <Brain className="w-3 h-3 text-white" />
            </div>
            <span className="text-[10px] text-slate-600">Axiom Intelligence · $5B corpus basis · 15+ hyperscale builds · 52 projects completed</span>
          </div>
          <div className="flex items-center gap-4">
            {['Site','Build','Commission','Capital','Exit'].map(ph => {
              const trig = POLICIES.filter(p => p.category === ph && p.status === 'Triggered').length;
              const cat = CAT_COLORS[ph];
              return (
                <div key={ph} className="flex items-center gap-1.5">
                  <div className={`w-1.5 h-1.5 rounded-full ${cat.dot} ${trig > 0 ? 'animate-pulse' : ''}`} />
                  <span className={`text-[9px] font-semibold uppercase ${cat.text}`}>{ph}</span>
                  {trig > 0 && <span className="text-[8px] text-red-400 font-bold">{trig}▲</span>}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AxiomStakeholder;
